import * as React from "react"
import { router } from "@inertiajs/react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

// ── shared types (the JSON shape ProductSnapshot serializes to) ──────────────
export type PageMeta = { total_count: number; page: number; per_page: number }
export type Period = { year: number; month: number }

export type Ref = { source_kind: string; source_id: number | null; dataset: string; period: string }

export type Identity = {
  product_variant_id: number
  canonical_name: string
  pack_size: string | null
  star_product_code: string | null
  is_own: boolean
  brand: string | null
  brand_canonical: string | null
  manufacturer: string | null
  tracked_category: string | null
}

export type Metrics = {
  doi_days: number | null
  doi_band: string
  sell_through_pct: number | null
  sell_through_band: string
  price_gap_pct: number | null
  price_gap_band: string
  discount_pct: number | null
  discount_band: string
  internal_share_pct: number | null
  internal_share_delta_pp: number | null
  internal_share_trend: string
  nielsen_share_pct: number | null
  nielsen_share_delta_pp: number | null
  nielsen_share_trend: string
}

export type ShareValue = { dimension: Record<string, string | null>; share_pct: number | null; refs?: Ref[] }
export type Nielsen = {
  period: { own_brand_share: ShareValue | null; competitors: ShareValue[]; refs: Ref[] }
  comparison: { own_brand_share: ShareValue | null; competitors: ShareValue[]; refs: Ref[] }
}

export type PriceOwn = {
  channel: string
  snapshot: string
  category_name: string | null
  shelf_price_idr: number | null
  promo_price_idr: number | null
  promo_period_label: string | null
  refs: Ref[]
}
export type PriceGap = {
  channel: string
  snapshot: string
  category_name: string | null
  own_price: number | null
  competitor_price: number | null
  gap_idr: number | null
  gap_pct: number | null
  refs: Ref[]
}
export type Activity = {
  activity_type: string | null
  description: string | null
  period_month: string | null
  source: string | null
  refs: Ref[]
}
export type DiscountHero = { account_name: string | null; max_disc_pct: number | null; hero_label: string | null; refs: Ref[] }
export type DiscountPlan = { account_name: string | null; product_count: number | null }

export type DriverFlag = { present: boolean; refs: Ref[] }
export type DriverEvidence = {
  present: boolean
  drivers: { price_promo: DriverFlag; planned_activity: DriverFlag; discount_plan: DriverFlag }
}

export type Snapshot = {
  product_variant: Identity
  period: Period
  comparison: Period
  sell_in: { period: { receipt_qty: number; net_qty: number; refs: Ref[] }; comparison: { receipt_qty: number; net_qty: number; refs: Ref[] } }
  stock: { period: { total_stock_qty: number; refs: Ref[] }; comparison: { total_stock_qty: number; refs: Ref[] } }
  service_level: {
    period: { po_qty: number; receipt_qty: number; service_level_pct: number | null; refs: Ref[] }
    comparison: { po_qty: number; receipt_qty: number; service_level_pct: number | null; refs: Ref[] }
  }
  sell_out: { period: { channel: string; qty_total_pcs: number; refs: Ref[] }; comparison: { channel: string; qty_total_pcs: number; refs: Ref[] } }
  internal_share: { period: ShareValue; comparison: ShareValue }
  nielsen: Nielsen
  price: { own: PriceOwn[]; gap: PriceGap[] }
  activity: { active: Activity[]; window: string; refs: Ref[] }
  discount: { fiscal_year: string; hero: DiscountHero[]; plans: DiscountPlan[]; refs: Ref[] }
  metrics: Metrics
  driver_evidence: DriverEvidence
  dq_flags: { metric: string; flag: string }[]
}

// ── tracked_category → human label ───────────────────────────────────────────
export const CATEGORY_LABELS: Record<string, string> = {
  female_fragrance: "Parfum Wanita",
  male_fragrance: "Parfum Pria",
  feminine_wash: "Feminine Wash",
  other: "Lainnya",
}
export function categoryLabel(value: string | null): string | null {
  if (!value) return null
  return CATEGORY_LABELS[value] ?? value
}

export function OwnBadge({ isOwn }: { isOwn: boolean }) {
  return isOwn ? <Badge tone="accent">Sendiri</Badge> : <Badge tone="muted">Kompetitor</Badge>
}

// ── band → {label, tone} for the deterministic metric classifications ────────
type Toned = { label: string; tone: React.ComponentProps<typeof Badge>["tone"] }
const BANDS: Record<string, Record<string, Toned>> = {
  doi: {
    stockout_risk: { label: "Risiko stok habis", tone: "danger" },
    overstock: { label: "Overstock", tone: "signal" },
    healthy: { label: "Sehat", tone: "success" },
    unknown: { label: "Tak ada data", tone: "muted" },
  },
  sell_through: {
    slow: { label: "Lambat", tone: "signal" },
    healthy: { label: "Sehat", tone: "success" },
    unknown: { label: "Tak ada data", tone: "muted" },
  },
  price_gap: {
    above_market: { label: "Di atas pasar", tone: "signal" },
    below_market: { label: "Di bawah pasar", tone: "accent" },
    at_market: { label: "Setara pasar", tone: "success" },
    unknown: { label: "Tak ada data", tone: "muted" },
  },
  discount: {
    deep: { label: "Diskon dalam", tone: "signal" },
    shallow: { label: "Diskon dangkal", tone: "neutral" },
    none: { label: "Tidak ada", tone: "muted" },
  },
  share: {
    up: { label: "Naik", tone: "success" },
    down: { label: "Turun", tone: "danger" },
    flat: { label: "Datar", tone: "muted" },
    unknown: { label: "Tak ada data", tone: "muted" },
  },
}
export function bandBadge(kind: keyof typeof BANDS, band: string) {
  const t = BANDS[kind][band] ?? { label: band, tone: "muted" as const }
  return <Badge tone={t.tone}>{t.label}</Badge>
}

// ── citation: every number traces to its source upload(s) ────────────────────
export function SourceRefs({ refs }: { refs: Ref[] }) {
  if (!refs || refs.length === 0) return null
  const seen = new Set<string>()
  const parts: string[] = []
  for (const r of refs) {
    const key = `${r.dataset}·${r.period}`
    if (!seen.has(key)) {
      seen.add(key)
      parts.push(key)
    }
  }
  return (
    <p className="mt-1 text-xs text-ink-muted" title={parts.join(" · ")}>
      Sumber: {parts.join(" · ")}
    </p>
  )
}

// ── number formatting ────────────────────────────────────────────────────────
const isNum = (value: unknown): value is number => typeof value === "number" && !Number.isNaN(value)

export function fmtInt(value: number | null | undefined): string {
  if (!isNum(value)) return "—"
  return new Intl.NumberFormat("id-ID").format(Math.round(value))
}
export function fmtPct(value: number | null | undefined, digits = 1): string {
  if (!isNum(value)) return "—"
  return `${value.toFixed(digits)}%`
}
export function fmtDelta(value: number | null | undefined): string {
  if (!isNum(value)) return "—"
  const sign = value > 0 ? "+" : ""
  return `${sign}${value.toFixed(2)} pp`
}
export function fmtIdr(value: number | null | undefined): string {
  if (!isNum(value)) return "—"
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(value)
}

// ── pagination + search (self-contained for this feature folder) ─────────────
export function Pagination({
  meta,
  path,
  query,
}: {
  meta: PageMeta
  path: string
  query: Record<string, string | number | undefined | null>
}) {
  const totalPages = Math.max(1, Math.ceil(meta.total_count / meta.per_page))
  if (totalPages <= 1) return null

  const go = (page: number) => {
    const q: Record<string, string> = {}
    Object.entries(query).forEach(([k, v]) => {
      if (v != null && v !== "") q[k] = String(v)
    })
    q.page = String(page)
    router.get(path, q, { preserveState: true, preserveScroll: true, replace: true })
  }

  return (
    <div className="mt-6 flex items-center justify-between text-sm text-ink-muted">
      <span>
        Halaman {meta.page} dari {totalPages} ({meta.total_count})
      </span>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" disabled={meta.page <= 1} onClick={() => go(meta.page - 1)}>
          Sebelumnya
        </Button>
        <Button variant="secondary" size="sm" disabled={meta.page >= totalPages} onClick={() => go(meta.page + 1)}>
          Berikutnya
        </Button>
      </div>
    </div>
  )
}

export function SearchForm({
  path,
  query,
  initial,
  placeholder,
}: {
  path: string
  query: Record<string, string | number | undefined | null>
  initial: string
  placeholder: string
}) {
  const [value, setValue] = React.useState(initial)
  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const q: Record<string, string> = {}
    Object.entries(query).forEach(([k, v]) => {
      if (v != null && v !== "") q[k] = String(v)
    })
    if (value.trim()) q.search = value.trim()
    else delete q.search
    router.get(path, q, { preserveState: true, preserveScroll: true, replace: true })
  }
  return (
    <form onSubmit={submit} className="flex-1 min-w-[220px]">
      <label htmlFor="pp-search">Cari</label>
      <Input
        id="pp-search"
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        className="mt-1"
      />
    </form>
  )
}

// ── M6: AI "why" narration (kill-switched) ───────────────────────────────────
export type NarrationStatus = "pending" | "narrating" | "draft" | "needs_review" | "published" | "failed"

export type NarrationSource = {
  dataset: string | null
  period: string | null
  source_kind: string | null
  source_id: number | null
}

export type Narration = {
  id: number
  status: NarrationStatus
  md_body: string | null
  model: string | null
  input_tokens: number
  output_tokens: number
  cost: number
  validation: { passed?: boolean; regenerated?: boolean; unknown_tokens?: string[] } | null
  error_message: string | null
  generated_at: string | null
  published_at: string | null
  period: Period
  sources: NarrationSource[]
}

// The narration is actively running in these states — the page polls while so.
export const NARRATION_ACTIVE: NarrationStatus[] = ["pending", "narrating"]

const NARRATION_STATUS_META: Record<NarrationStatus, { label: string; tone: React.ComponentProps<typeof Badge>["tone"] }> = {
  pending: { label: "Antre", tone: "muted" },
  narrating: { label: "Menarasikan", tone: "accent" },
  draft: { label: "Draf", tone: "accent" },
  needs_review: { label: "Perlu Tinjau", tone: "signal" },
  published: { label: "Terbit", tone: "success" },
  failed: { label: "Gagal", tone: "danger" },
}

export function NarrationStatusBadge({ status }: { status: NarrationStatus }) {
  const meta = NARRATION_STATUS_META[status] ?? NARRATION_STATUS_META.pending
  return <Badge tone={meta.tone}>{meta.label}</Badge>
}

const NUMBER_RE = /(\d[\d.,]*\d|\d)/

// Render the cited prose as paragraphs. On a needs_review narration, numbers the
// validator could not ground are highlighted amber so they can be corrected.
export function NarratedProse({ text, unknownTokens = [] }: { text: string; unknownTokens?: string[] }) {
  const paras = (text ?? "").trim().split(/\n{2,}/).filter(Boolean)
  if (paras.length === 0) return <p className="text-ink-muted">—</p>
  return (
    <div className="space-y-3 text-ink-body">
      {paras.map((para, i) => (
        <p key={i}>{annotateUnknown(para, unknownTokens)}</p>
      ))}
    </div>
  )
}

function annotateUnknown(text: string, unknownTokens: string[]) {
  if (unknownTokens.length === 0) return text
  return text.split(NUMBER_RE).map((part, i) =>
    i % 2 === 1 && unknownTokens.includes(part) ? (
      <span
        key={i}
        className="rounded bg-amber-100 px-0.5 font-medium text-amber-700 tabular-nums dark:bg-amber-900/40 dark:text-amber-300"
        title="Angka ini tidak dapat dicocokkan ke context pack — perlu ditinjau."
      >
        {part}
      </span>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  )
}

// Distinct "dataset · period" labels from a narration's grounded citations.
export function narrationSourceLabels(sources: NarrationSource[]): string[] {
  const seen = new Set<string>()
  const parts: string[] = []
  for (const s of sources) {
    const key = `${s.dataset ?? "—"}·${s.period ?? "—"}`
    if (!seen.has(key)) {
      seen.add(key)
      parts.push(key)
    }
  }
  return parts
}
